import { Link, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import { DataTable } from "@/common/components/DataTable/DataTable";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import TimestampCell from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSafeDataTableStateProps } from "@/common/hooks/useDataTableParamState";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import { linkClickModifierHandler } from "@/common/utils/linkClickHandler";
import { shouldIgnoreRowNavigation } from "@/common/utils/rowNavigation";
import { CompanyUsersEmptyState } from "@/company/components/CompanyUsersEmptyState";
import { useCompanyUsersTableState } from "@/company/hooks/useCompanyTableStates";
import { UserUrl } from "@reflag/shared/urls";
import { UserDTO, UserPage, UsersSortByColumn } from "@reflag/shared/userAPI";

type Props = {
  data?: UserPage;
  totalCount?: number;
  countError?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

export function CompanyUsersTable({
  data,
  totalCount,
  countError,
  isLoading,
  isFetching,
  isError,
  onRetry,
}: Props) {
  const { env } = useEnvironment();
  const navigate = useNavigate();
  const tableState = useCompanyUsersTableState();
  // We don't want to pass along invalid props to the data table
  const safeTableProps = useSafeDataTableStateProps(tableState);

  const columnHelper = createSortableColumnHelper<UserDTO, UsersSortByColumn>();
  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ row }) => <UserDisplay user={row.original} />,
      }),
      columnHelper.accessor("id", {
        header: "ID",
        cell: (cell) => <Text fontSize="sm">{cell.getValue()}</Text>,
      }),
      columnHelper.accessor("firstSeen", {
        header: "First seen",
        sortDescFirst: true,
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return <TimestampCell value={value} />;
          }

          return <NotAvailableCell />;
        },
      }),
      columnHelper.accessor("lastSeen", {
        header: "Last seen",
        sortDescFirst: true,
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return <TimestampCell value={value} />;
          }

          return <NotAvailableCell />;
        },
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return (
              <Link fontSize="sm" href={`mailto:${value}`}>
                {value}
              </Link>
            );
          }

          return <NotAvailableCell />;
        },
      }),
    ];
  }, [columnHelper]);

  const users = data?.data || [];

  return (
    <DataTable
      columns={columns}
      data={users}
      emptyState={
        <CompanyUsersEmptyState
          isError={isError}
          isLoading={isLoading}
          onRetry={onRetry}
        />
      }
      isFetching={isFetching}
      meta={data?.metadata}
      pageSize={50}
      tableId="company-users"
      toolbarLeftActions={() => null}
      toolbarRightActions={
        countError ? (
          <Text color="dimmed" fontSize="sm">
            Count unavailable
          </Text>
        ) : undefined
      }
      totalCount={totalCount}
      variant="clickable"
      canCustomize
      canPaginate
      canSort
      onRowClick={(row, e) => {
        if (shouldIgnoreRowNavigation(e.target)) {
          return;
        }
        const url = UserUrl(env, row.original.id);
        e.preventDefault();
        linkClickModifierHandler(url, () => {
          navigate(url);
        })(e);
      }}
      {...safeTableProps}
    />
  );
}
